import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import {pushEdapEvent} from '../../../utils/edap';
import {fetchImages} from '../../../actions/destination-images';
import {CardCarousel} from '../../common/carousel/Carousel';
import {RatioCard} from '../../common/carousel/Card';
import {TABLET_BREAKPOINT} from '../../../utils/constants';
import {buildSerpUrl} from '../../../utils/urls';
import EdapViewTracker from '../../common/edap-view-tracker/EdapViewTracker';

const ACTION_LOCATION = 'city-descendant-linkpile';

function handleClick(lbsId, position) {
    const eventData = {
        actionlocation: ACTION_LOCATION,
        eventlbsuuid: lbsId,
        eventrefinements: `locality,position:${position}`,
    };

    const fastPathEventData = {
        action_location: ACTION_LOCATION,
        event_lbsuuid: lbsId,
        event_refinements: `locality,position:${position}`,
    };

    return () => {
        pushEdapEvent('content_tile.selected', fastPathEventData);
        pushEdapEvent('destination.link.click', eventData);
    };
}

export function replaceTemplateString(template, res) {
    return (template || '').replace('${templateId}', `carousel-${res}`);
}

function getLbsIds(items = []) {
    return items.map(({lbsId}) => lbsId);
}

function ensureCorrectSize(popularDestinations) {
    return popularDestinations.length >= 4
        ? popularDestinations.slice(0, 10)
        : [];
}

function getPopularDestinations(items = [], images = []) {
    const imagesById = images.reduce(
        (acc, item) => ({
            ...acc,
            [item.key]: item.urlTemplate,
        }),
        {}
    );

    const popularDestinations = items.reduce((acc, item) => {
        const urlTemplate = imagesById[item.lbsId];

        if (urlTemplate) {
            acc.push({
                ...item,
                getImageUrl: replaceTemplateString.bind(null, urlTemplate),
            });
        }

        return acc;
    }, []);

    return ensureCorrectSize(popularDestinations);
}

function createRatioCard({isMobile, search, host, basePath}) {
    return (props, index) => {
        const {
            fullName,
            getImageUrl,
            imageUrlTemplate,
            lbsId,
            simpleName,
            subheading,
            title,
        } = props;

        const {arrivalDate, departureDate} = search;

        let imageUrl;
        if (getImageUrl) {
            imageUrl = getImageUrl;
        } else if (!getImageUrl && imageUrlTemplate) {
            imageUrl = replaceTemplateString.bind(null, imageUrlTemplate);
        }

        const urlPath = buildSerpUrl({
            basePath,
            host,
            params: {
                q: fullName,
                arrival: arrivalDate,
                departure: departureDate,
            },
        });

        return (
            <RatioCard
                altText={simpleName}
                footer={subheading}
                getImageUrl={imageUrl}
                isMobile={isMobile}
                key={index}
                linkRel={null}
                onClick={handleClick(lbsId, index + 1)}
                path={urlPath}
                title={title}
            />
        );
    };
}

export class PopularDestinationsComponent extends PureComponent {
    componentDidMount() {
        const {
            basePathPrefix,
            lbsIds = [],
            popularDestinations = [],
        } = this.props;

        if (!popularDestinations.length && lbsIds.length) {
            this.props.dispatch(fetchImages({basePathPrefix, lbsIds}));
        }
    }

    handleNext = () => {
        const edapData = {
            actionlocation: ACTION_LOCATION,
        };

        pushEdapEvent('nav.next.click', edapData);
        pushEdapEvent('ux.cards_visible.adjusted', {
            action_location: ACTION_LOCATION,
            event_action: 'next_arrow_clicked',
        });
    };

    handlePrev = () => {
        const edapData = {
            actionlocation: ACTION_LOCATION,
        };

        pushEdapEvent('nav.previous.click', edapData);
        pushEdapEvent('ux.cards_visible.adjusted', {
            action_location: ACTION_LOCATION,
            event_action: 'previous_arrow_clicked',
        });
    };

    render() {
        const {
            cityCarouselTitle,
            isMobile,
            popularDestinations = [],
            search,
            basePath,
            host,
            moduleHeading,
            showNewHeadings,
        } = this.props;

        if (!popularDestinations.length) {
            return null;
        }

        const cards = popularDestinations.map(
            createRatioCard({isMobile, search, host, basePath})
        );

        return (
            <div className="container module module--no-padding popular-destinations-ratio-cards">
                <EdapViewTracker
                    eventData={{
                        action_location: ACTION_LOCATION,
                        position_fired: 'top',
                    }}
                    edapEvent={'content_module.presented'}
                />
                {showNewHeadings && (
                    <h2 className="page-heading heading--with-padding">
                        {moduleHeading}
                    </h2>
                )}
                {showNewHeadings && (
                    <h3 className="page-subheading heading--with-padding">
                        {cityCarouselTitle}
                    </h3>
                )}
                <CardCarousel
                    titleMessage={showNewHeadings ? null : cityCarouselTitle}
                    onNextClick={this.handleNext}
                    onPreviousClick={this.handlePrev}
                >
                    {cards}
                </CardCarousel>
                <EdapViewTracker
                    eventData={{
                        action_location: ACTION_LOCATION,
                        position_fired: 'bottom',
                    }}
                    edapEvent={'content_module.presented'}
                />
                <EdapViewTracker
                    eventData={{actionlocation: ACTION_LOCATION}}
                />
            </div>
        );
    }
}

export function mapStateToProps({
    destination,
    config,
    search,
    abacus,
    modules,
}) {
    const {locality, details, destinationImages} = destination;
    const cityCarouselTitle = locality.title || '';
    const pageHeadings = modules.pageHeadings || {};
    const popularDestinations = getPopularDestinations(
        locality.carousel,
        destinationImages
    );

    return {
        abacus,
        basePath: config.site.basePath,
        basePathPrefix: config.url.basePathPrefix,
        cityCarouselTitle,
        host: config.site.domain,
        lbsIds: getLbsIds(locality.carousel),
        popularDestinations,
        search,
        simpleName: (details && details.simpleName) || '',
        moduleHeading: pageHeadings.popularDestinations || '',
        showNewHeadings: pageHeadings.showNewHeadings || false,
    };
}

export const PopularDestinations = connect(mapStateToProps)(
    determineIfMobile(PopularDestinationsComponent, TABLET_BREAKPOINT)
);

PopularDestinations.displayName = 'PopularDestinations';

export default PopularDestinations;
